import { type ReactNode, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from '@/contexts/root-store-context'
import * as GlobalNavigationBar from '@/components/GlobalNavigationBar'
import { links } from '@/utils/link'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useProvidePageBottomOffset } from '@/contexts/page-bottom-offset-context'
import { useProvideScrollPaddingBottom } from '@/contexts/scroll-padding-context'
import { useObservableValue, useSubject } from '@/hooks/rx-hooks'
import { createLayeredContext } from '@/contexts/layered-context'
import type { ShellInfo } from '../ShellInfo'
import { useNavigation } from 'react-router'
import { BehaviorSubject, delay, of, switchMap } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { useProvideSupportWidgetBottomOffset } from '@/features/support/components/ChatSupportSpaceManager'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { useSessionOptions } from '@/features/iam/auth/session-options-hooks'
import { type SessionOptions } from '@/features/iam/auth/SessionOptions'

// Used to control the viewport background from child components.
const {
  Provider: ViewportBackgroundContextProvider,
  useProvide: useProvideViewportBackgroundValue,
  useValue: useViewportBackgroundValue,
} = createLayeredContext<'primary' | 'secondary' | null>('primary', {
  excludeNulls: true,
})

// Used to hide the navbar from within a view.
const {
  Provider: GlobalNavbarHiddenContextProvider,
  useProvide: useProvideGlobalNavbarHiddenValue,
  useValue: useGlobalNavbarHidden,
} = createLayeredContext<boolean | null>(false, {
  excludeNulls: true,
})

// Used to disable the global navigation indicator from within a view.
const {
  Provider: GlobalNavigationIndicatorDisabledContextProvider,
  useProvide: useProvideGlobalNavigationIndicatorDisabledValue,
  useValue: useGlobalNavigationIndicatorDisabled,
} = createLayeredContext<boolean | null>(false, {
  excludeNulls: true,
})

export interface GlobalNavigationContainerProps {
  children?: ReactNode
}

/**
 * Global navigation shell which contains the navigation bar
 * and main viewport.
 */
export const GlobalNavigationContainer = observer(
  function GlobalNavigationContainer({
    children,
  }: GlobalNavigationContainerProps) {
    const {
      shell: { shellInfo },
      activity: { unreadNotificationsCount$ },
    } = useRootStore()

    const sessionOptions = useSessionOptions()

    const isSpacious = useIsSpacious()

    const bottomOffset =
      sessionOptions.navbarOptions.kind === 'None' || isSpacious
        ? null
        : 'var(--global-navbar-height)'

    useProvidePageBottomOffset(bottomOffset)
    useProvideScrollPaddingBottom(bottomOffset)
    useProvideSupportWidgetBottomOffset(bottomOffset, -1)

    const unreadNotificationsCount = useObservableValue(
      unreadNotificationsCount$,
      { initial: 0 },
    )

    return (
      <GlobalNavbarHiddenContextProvider>
        <GlobalNavigationIndicatorDisabledContextProvider>
          <ViewportBackgroundContextProvider>
            <GlobalNavbarRootContainer
              sessionOptions={sessionOptions}
              unreadNotificationsCount={unreadNotificationsCount}
              shellInfo={shellInfo}
            >
              {children}
            </GlobalNavbarRootContainer>
          </ViewportBackgroundContextProvider>
        </GlobalNavigationIndicatorDisabledContextProvider>
      </GlobalNavbarHiddenContextProvider>
    )
  },
)

/**
 * Allows a child component to hide the global navbar.
 *
 * @param hidden
 */
export function useHideGlobalNavbar(hidden: boolean | null) {
  useProvideGlobalNavbarHiddenValue(hidden, hidden ? 1 : 0)
}

/**
 * Allows a child component to disable the global navigation indicator.
 *
 * @param disabled
 */
export function useDisableGlobalNavigationIndicator(disabled: boolean | null) {
  useProvideGlobalNavigationIndicatorDisabledValue(disabled, disabled ? 1 : 0)
}

interface GlobalNavbarListContainerProps {
  sessionOptions: SessionOptions
  unreadNotificationsCount: number
  shellInfo: ShellInfo
  children: ReactNode
}

/**
 * Global Navbar List Container
 */
const GlobalNavbarRootContainer = observer(function GlobalNavbarListContainer({
  sessionOptions,
  unreadNotificationsCount,
  shellInfo,
  children,
}: GlobalNavbarListContainerProps) {
  const isHidden = useGlobalNavbarHidden()
  const isSpacious = useIsSpacious()
  const background = useViewportBackgroundValue()
  const isIndicatorDisabled = useGlobalNavigationIndicatorDisabled()
  const lightDark = useLightDarkFn()

  const navigation = useNavigation()
  const isNavigating$ = useSubject(() => new BehaviorSubject<boolean>(false))
  useEffect(() => {
    isNavigating$.next(Boolean(navigation.location) && !isIndicatorDisabled)
  }, [isNavigating$, navigation, isIndicatorDisabled])

  // There are cases where a route loader will trigger very briefly due to
  // awaiting promises, despite those promises resolving basically
  // in the next tick. To avoid flashing the loading indicator for those,
  // we'll delay all transitions to `true` by a couple of milliseconds.
  const isNavigating = useObservableValue(
    useMemo(() => {
      return isNavigating$.pipe(
        switchMap((isNavigating) =>
          !isNavigating ? of(false) : of(true).pipe(delay(20)),
        ),
        distinctUntilChanged(),
      )
    }, [isNavigating$]),
  )

  return (
    <GlobalNavigationBar.Root isNavigating={isNavigating}>
      {sessionOptions.navbarOptions.kind === 'Default' && !isHidden && (
        <GlobalNavigationBar.List isNavigationIndicator={!isSpacious}>
          <GlobalNavigationBar.Logo
            alt={shellInfo.name}
            src={lightDark(shellInfo.iconUrl, shellInfo.invertedIconUrl)}
            to={sessionOptions.returnOptions?.url ?? links.home()}
          />
          <GlobalNavigationBar.Item
            label="Home"
            icon="nav-home"
            iconActive="nav-home-filled"
            to={links.home()}
          />
          <GlobalNavigationBar.Item
            label="Jobs"
            icon="nav-jobs"
            iconActive="nav-jobs-filled"
            to={links.jobs()}
          />
          <GlobalNavigationBar.Item
            label="Docs"
            icon="nav-docs"
            iconActive="nav-docs-filled"
            to={links.docs()}
          />
          <GlobalNavigationBar.Spacer />

          <GlobalNavigationBar.Item
            label="Activity"
            icon="nav-activity"
            iconActive="nav-activity-filled"
            badgeLabel={
              unreadNotificationsCount === 0
                ? undefined
                : `${unreadNotificationsCount} unread notifications`
            }
            to={links.activity()}
          />
          <GlobalNavigationBar.Item
            label="Account"
            icon="nav-account"
            iconActive="nav-account-filled"
            to={isSpacious ? links.accountPersonalInfo() : links.account()}
          />
        </GlobalNavigationBar.List>
      )}
      <GlobalNavigationBar.Viewport
        isNavigationIndicator={isSpacious}
        background={background ?? 'primary'}
      >
        {children}
      </GlobalNavigationBar.Viewport>
    </GlobalNavigationBar.Root>
  )
})

/**
 * Changes the background color of the main viewport of the navbar.
 *
 * @param background
 */
export function useProvideViewportBackground(
  background: 'primary' | 'secondary' | null,
) {
  useProvideViewportBackgroundValue(background)
}
